/**
 * DraftsTab — every non-archived Content Studio draft with a thumbnail
 * preview of slide 0 (or the strip of slides for carousels). Click a card
 * to expand caption + hashtags + download links; archive to hide.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Archive, Calendar, Check, Copy, Download, ImageIcon, Layers, Loader2, Package, Send, Sparkles } from 'lucide-react';
import JSZip from 'jszip';
import { marketing } from '@/lib/supabase/marketing';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn, errorMessage } from '@/lib/utils';
import { useContentDrafts, useArchiveDraft, type DraftRow, type DraftStatus } from '@/hooks/useContentDrafts';
import { BestSlotChips } from './BestSlotChips';

// publish_error is a text column — if the worker ever stored "[object Object]"
// or the JSON form of a Meta Graph API error, salvage something readable.
function formatStoredError(stored: string): string {
  const s = stored.trim();
  if (s === '[object Object]') return 'publish failed (details lost — check Supabase logs for content-schedule-worker)';
  if (s.startsWith('{') || s.startsWith('[')) {
    try { return errorMessage(JSON.parse(s), s); } catch { return s; }
  }
  return s;
}

export function DraftsTab() {
  const q = useContentDrafts();
  const archive = useArchiveDraft();
  const [openId, setOpenId] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-40 w-full" />)}
      </div>
    );
  }
  if (q.isError) return <EmptyState title="Couldn't load drafts" description={String(q.error)} />;
  const drafts = q.data ?? [];
  if (drafts.length === 0) {
    return <EmptyState title="No drafts yet" description="Compose one in the Composer tab. Saved drafts show up here." />;
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        {drafts.length} draft{drafts.length === 1 ? '' : 's'} · click to open
      </div>
      <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {drafts.map((d) => (
          <DraftCard
            key={d.id}
            draft={d}
            open={openId === d.id}
            onToggle={() => setOpenId(openId === d.id ? null : d.id)}
            onArchive={() => archive.mutate(d.id)}
          />
        ))}
      </div>
    </div>
  );
}

function DraftCard({
  draft, open, onToggle, onArchive,
}: {
  draft: DraftRow;
  open: boolean;
  onToggle: () => void;
  onArchive: () => void;
}) {
  const rendered = draft.slides.filter((s) => s.image_r2_key).length;
  const heroKey = draft.slides.find((s) => s.slide_index === 0)?.image_r2_key ?? null;
  const isCarousel = draft.post_kind === 'carousel';

  async function downloadZip() {
    const zip = new JSZip();
    for (const sl of draft.slides) {
      if (!sl.image_r2_key) continue;
      const r = await fetch(`/api/media/${sl.image_r2_key}`);
      if (!r.ok) continue;
      const buf = new Uint8Array(await r.arrayBuffer());
      const idx = String(sl.slide_index + 1).padStart(2, '0');
      zip.file(`slide-${idx}.jpg`, buf);
    }
    if (draft.caption) zip.file('caption.txt', `${draft.caption}\n\n${draft.hashtags.join(' ')}`);
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flax-${draft.bucket_slug}-${draft.id.slice(0, 8)}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card className={cn('overflow-hidden', open && 'col-span-full')}>
      {open ? (
        <div className="grid gap-3 p-3 md:grid-cols-[220px_minmax(0,1fr)]">
          <div className="space-y-2">
            <button type="button" onClick={onToggle} className="block w-full text-left">
              <div className="relative aspect-square overflow-hidden rounded-md bg-muted/20">
                {heroKey ? (
                  <img src={`/api/media/${heroKey}`} alt={draft.bucket_slug} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                {isCarousel && (
                  <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/60 px-2 py-0.5 text-2xs text-white backdrop-blur">
                    <Layers className="h-3 w-3" /> {rendered}/{draft.slides.length}
                  </div>
                )}
                <div className="absolute left-1.5 top-1.5">
                  <StatusBadge status={draft.status} scheduledAt={draft.scheduled_at} />
                </div>
              </div>
            </button>
            <div className="flex flex-wrap items-center gap-1 text-2xs text-muted-foreground">
              <span className="rounded bg-muted/50 px-1.5 py-0.5 uppercase">{draft.bucket_slug}</span>
              <span className="capitalize">{draft.post_kind}</span>
              <span className="ml-auto tabular-nums opacity-80">
                {new Date(draft.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>
            </div>
          </div>
          <ExpandedBody draft={draft} rendered={rendered} isCarousel={isCarousel} downloadZip={downloadZip} onArchive={onArchive} />
        </div>
      ) : (
      <button type="button" onClick={onToggle} className="block w-full text-left">
        <div className="relative aspect-square bg-muted/20">
          {heroKey ? (
            <img src={`/api/media/${heroKey}`} alt={draft.bucket_slug} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
          {isCarousel && (
            <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/60 px-2 py-0.5 text-2xs text-white backdrop-blur">
              <Layers className="h-3 w-3" /> {rendered}/{draft.slides.length}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-2xs text-white">
            <span className="rounded bg-white/20 px-1.5 py-0.5 uppercase">{draft.bucket_slug}</span>
            <span className="capitalize">{draft.post_kind}</span>
            <span className="ml-auto tabular-nums opacity-80">
              {new Date(draft.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </span>
          </div>
          <div className="absolute left-1.5 top-1.5">
            <StatusBadge status={draft.status} scheduledAt={draft.scheduled_at} />
          </div>
        </div>
      </button>
      )}
    </Card>
  );
}

function ExpandedBody({
  draft, rendered, isCarousel, downloadZip, onArchive,
}: {
  draft: DraftRow;
  rendered: number;
  isCarousel: boolean;
  downloadZip: () => void | Promise<void>;
  onArchive: () => void;
}) {
  const qc = useQueryClient();
  const [captionEdit, setCaptionEdit] = useState(draft.caption ?? '');
  const [hashtagsEdit, setHashtagsEdit] = useState((draft.hashtags ?? []).join(' '));
  const [captionSaving, setCaptionSaving] = useState(false);
  const [captionDrafting, setCaptionDrafting] = useState(false);
  const [scheduleAt, setScheduleAt] = useState<string>(defaultScheduleAt(draft.scheduled_at));
  const [busy, setBusy] = useState<null | 'schedule' | 'publish'>(null);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  async function draftCaption() {
    setCaptionDrafting(true);
    setStatus(null);
    try {
      const r = await fetch('/api/content/draft-caption', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ draft_id: draft.id }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? `HTTP ${r.status}`);
      setCaptionEdit(j.caption ?? '');
      setHashtagsEdit((j.hashtags ?? []).join(' '));
      await qc.invalidateQueries({ queryKey: ['content-drafts'] });
    } catch (e) {
      setStatus({ ok: false, msg: (e as Error).message });
    } finally {
      setCaptionDrafting(false);
    }
  }

  async function saveCaption() {
    setCaptionSaving(true);
    try {
      const hashtags = hashtagsEdit.split(/\s+/).filter(Boolean);
      const { error } = await marketing
        .from('content_draft')
        .update({ caption: captionEdit, hashtags })
        .eq('id', draft.id);
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ['content-drafts'] });
    } finally {
      setCaptionSaving(false);
    }
  }

  async function schedule() {
    setBusy('schedule');
    setStatus(null);
    try {
      const iso = new Date(scheduleAt).toISOString();
      const { error } = await marketing
        .from('content_draft')
        .update({ status: 'scheduled', scheduled_at: iso, publish_error: null })
        .eq('id', draft.id);
      if (error) throw error;
      setStatus({ ok: true, msg: `Scheduled for ${new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` });
      await qc.invalidateQueries({ queryKey: ['content-drafts'] });
    } catch (e) {
      setStatus({ ok: false, msg: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function publishNow() {
    setBusy('publish');
    setStatus(null);
    try {
      const { error } = await marketing
        .from('content_draft')
        .update({ status: 'scheduled', scheduled_at: new Date().toISOString(), publish_error: null })
        .eq('id', draft.id);
      if (error) throw error;
      const r = await fetch('/api/content/publish', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ draft_id: draft.id }),
      });
      const j = await r.json();
      const first = j.results?.[0];
      if (!r.ok || j.error) throw new Error(errorMessage(j.error, `HTTP ${r.status}`));
      if (first && !first.ok) throw new Error(errorMessage(first.error, 'publish failed'));
      setStatus({ ok: true, msg: `Published · media id ${first?.media_id ?? '—'}` });
      await qc.invalidateQueries({ queryKey: ['content-drafts'] });
    } catch (e) {
      setStatus({ ok: false, msg: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  const captionDirty = captionEdit !== (draft.caption ?? '') || hashtagsEdit !== (draft.hashtags ?? []).join(' ');
  const isPublished = draft.status === 'published';
  const isScheduled = draft.status === 'scheduled';

  return (
    <div className="space-y-3 text-sm">
      {isCarousel && draft.slides.length > 1 && (
        <div className="flex gap-1 overflow-x-auto">
          {draft.slides.map((sl) => (
            <div key={sl.slide_index} className="h-14 w-14 shrink-0 overflow-hidden rounded border border-border/40 bg-muted/30">
              {sl.image_r2_key ? (
                <img src={`/api/media/${sl.image_r2_key}`} alt={`slide ${sl.slide_index + 1}`} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-2xs text-muted-foreground/60">
                  {sl.slide_index + 1}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Caption block */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-2xs text-muted-foreground">
          <span>Caption</span>
          {draft.caption && (
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(`${draft.caption}\n\n${draft.hashtags.join(' ')}`)}
              className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted"
              title="Copy caption + hashtags"
            >
              <Copy className="h-3 w-3" /> copy
            </button>
          )}
        </div>
        {draft.caption || captionDrafting ? (
          <>
            <textarea
              rows={4}
              value={captionEdit}
              onChange={(e) => setCaptionEdit(e.target.value)}
              className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
              placeholder="Caption will appear here…"
            />
            <textarea
              rows={2}
              value={hashtagsEdit}
              onChange={(e) => setHashtagsEdit(e.target.value)}
              className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 font-mono text-2xs"
              placeholder="#hashtags space-separated"
            />
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={draftCaption} disabled={captionDrafting}>
                {captionDrafting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                {draft.caption ? 'Redo caption' : 'Draft with Claude'}
              </Button>
              {captionDirty && (
                <Button size="sm" onClick={saveCaption} disabled={captionSaving}>
                  {captionSaving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                  Save
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border/50 bg-muted/20 px-3 py-2 text-2xs">
            <span className="text-muted-foreground">No caption yet.</span>
            <Button size="sm" variant="outline" onClick={draftCaption} disabled={rendered === 0}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Draft with Claude
            </Button>
            {rendered === 0 && <span className="text-muted-foreground/60">(generate an image first)</span>}
          </div>
        )}
      </div>

      {draft.publish_error && (
        <div className="flex items-start gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/5 px-2.5 py-1.5 text-2xs text-rose-500">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{formatStoredError(draft.publish_error)}</span>
        </div>
      )}

      {/* Schedule / publish block — only if caption + images are ready */}
      {!isPublished && rendered > 0 && (
        <div className="space-y-2 rounded-md border border-border/40 bg-muted/10 p-2">
          <div className="text-2xs font-medium text-muted-foreground">
            {isScheduled ? 'Currently scheduled' : 'Schedule to publish'}
          </div>
          <BestSlotChips selected={scheduleAt} onPick={setScheduleAt} />
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-xs"
            />
            <Button size="sm" variant="outline" onClick={schedule} disabled={busy !== null}>
              {busy === 'schedule' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Calendar className="mr-1.5 h-3.5 w-3.5" />}
              {isScheduled ? 'Update time' : 'Schedule'}
            </Button>
            <div className="h-4 w-px bg-border/50" />
            <Button size="sm" onClick={publishNow} disabled={busy !== null || !draft.caption}>
              {busy === 'publish' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              Publish now
            </Button>
            {!draft.caption && <span className="text-2xs text-muted-foreground">(needs a caption to publish)</span>}
          </div>
        </div>
      )}

      {status && (
        <div className={cn(
          'rounded-md border px-3 py-2 text-xs',
          status.ok ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400' : 'border-rose-500/40 bg-rose-500/5 text-rose-500',
        )}>
          {status.msg}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {rendered > 0 && (
          <Button size="sm" variant="outline" onClick={downloadZip}>
            {isCarousel ? <Package className="mr-1.5 h-3.5 w-3.5" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
            Download{isCarousel ? ' ZIP' : ''}
          </Button>
        )}
        {isPublished && draft.windsor_media_id && (
          <a
            href={`https://www.instagram.com/reel/${draft.windsor_media_id}/`}
            target="_blank" rel="noreferrer"
            className="text-2xs text-muted-foreground underline hover:text-foreground"
          >
            live on Instagram — boost from IG grid tab
          </a>
        )}
        <Button size="sm" variant="ghost" onClick={onArchive}>
          <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
        </Button>
      </div>
    </div>
  );
}


function defaultScheduleAt(existing: string | null): string {
  const base = existing ? new Date(existing) : new Date(Date.now() + 60 * 60_000);
  base.setSeconds(0, 0);
  if (!existing) base.setMinutes(Math.ceil(base.getMinutes() / 5) * 5);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T${pad(base.getHours())}:${pad(base.getMinutes())}`;
}

function StatusBadge({ status, scheduledAt }: { status: DraftStatus; scheduledAt: string | null }) {
  const cfg: Record<DraftStatus, { label: string; className: string; icon?: typeof Check }> = {
    draft:         { label: 'draft',      className: 'bg-white/20 text-white' },
    prompt_ready:  { label: 'prompt',     className: 'bg-white/20 text-white' },
    image_ready:   { label: 'image',      className: 'bg-white/20 text-white' },
    caption_ready: { label: 'ready',      className: 'bg-white/20 text-white' },
    scheduled:     { label: 'scheduled',  className: 'bg-sky-500 text-white', icon: Calendar },
    published:     { label: 'published',  className: 'bg-emerald-500 text-white', icon: Check },
    failed:        { label: 'failed',     className: 'bg-rose-500 text-white', icon: AlertCircle },
    archived:      { label: 'archived',   className: 'bg-neutral-500 text-white' },
  };
  const c = cfg[status] ?? cfg.draft;
  const Icon = c.icon;
  return (
    <div className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium backdrop-blur', c.className)} title={scheduledAt ? new Date(scheduledAt).toLocaleString('en-IN') : status}>
      {Icon && <Icon className="h-3 w-3" />}
      {c.label}
      {status === 'scheduled' && scheduledAt && (
        <span className="ml-0.5 opacity-80 tabular-nums">
          {new Date(scheduledAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  );
}
